<template>
  <div class="field-conditional-display">
    <div class="field-header my-3">
      <label class="field-label">
        <i class="fas fa-bolt conditional-icon fa-fw" />
        {{ t('templateBuilder.conditionalDisplay.label') }}
      </label>
      <div class="header-actions">
        <Button
          :label="hasSegments ? t('templateBuilder.conditionalDisplay.modify') : t('templateBuilder.conditionalDisplay.configure')"
          :icon="hasSegments ? 'far fa-edit' : 'far fa-plus'"
          class="p-button-sm p-button-text btn-action-green"
          @click="openModal"
        />
        <Button
          v-if="hasSegments"
          icon="far fa-trash-alt"
          class="p-button-sm p-button-secondary p-button-danger"
          @click="deleteConfiguration"
        />
      </div>
    </div>

    <div
      v-if="hasSegments && segmentCount > 0"
      class="segments-summary"
    >
      <div class="segments-info">
        <i class="far fa-check-circle text-success mr-2" />
        <span class="font-semibold">{{ segmentCount }}</span>
        <span class="ml-1">{{ t('templateBuilder.conditionalDisplay.segmentsActive') }}</span>
      </div>
    </div>

    <div
      v-else
      class="no-segments"
    >
      <i class="far fa-info-circle text-muted mr-2" />
      <span class="text-muted">{{ t('templateBuilder.conditionalDisplay.noSegments') }}</span>
    </div>

    <ConditionalDisplayModal
      v-model:visible="isModalVisible"
      :model-value="currentSegments || [[{}]]"
      :context-element="contextElement"
      @validate="handleSave"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType, ref, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import ConditionalDisplayModal from '@/components/conditional-display/ConditionalDisplayModal.vue';
import { FieldConfig, ParserFieldObject } from '@/types';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

export default defineComponent({
  name: 'FormFieldConditionalDisplay',

  components: {
    Button,
    ConditionalDisplayModal,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const isModalVisible = ref(false);
    const contextElement = ref<HTMLElement | null>(null);

    const property = computed(() => props.parserValues.properties?.[0]);

    // Get the HTML element from the selector
    const getContextElement = () => {
      if (!props.parserValues.selector) return null;

      const template = getTemplateIframeDocument();
      if (!template) return null;

      // The selector is already processed with the actual ID by the parser
      return template.querySelector(props.parserValues.selector) as HTMLElement | null;
    };

    const currentSegments = computed(() => {
      if (!property.value?.value) return null;

      const rawValue = property.value.value;

      // If already an object/array, return it directly
      if (typeof rawValue === 'object' && rawValue !== null) {
        return rawValue;
      }

      // If string, parse it
      if (typeof rawValue === 'string') {
        try {
          const parsed = JSON.parse(rawValue);
          return parsed;
        } catch (e) {
          return null;
        }
      }

      return null;
    });

    const hasSegments = computed(() => currentSegments.value && Array.isArray(currentSegments.value) && currentSegments.value.length > 0);

    const segmentCount = computed(() => {
      if (!currentSegments.value) return 0;
      // Count only first-level blocks (AND blocks)
      return currentSegments.value.filter((blockAND: any[]) => Array.isArray(blockAND)
        && blockAND.length > 0
        && blockAND.some((block: any) => block && typeof block === 'object' && Object.keys(block).length > 0)).length;
    });

    const openModal = () => {
      contextElement.value = getContextElement();
      isModalVisible.value = true;
    };

    const handleSave = (segments: any) => {
      if (property.value) {
        property.value.value = segments && segments.length > 0 ? JSON.stringify(segments) : '';

        emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });
      }
    };

    const deleteConfiguration = () => {
      if (property.value) {
        // Set value to empty string to remove s-segments attribute
        property.value.value = '';

        emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });
      }
    };

    return {
      t,
      isModalVisible,
      contextElement,
      currentSegments,
      hasSegments,
      segmentCount,
      openModal,
      handleSave,
      deleteConfiguration,
    };
  },
});
</script>

<style scoped lang="scss">
.field-conditional-display {
  .field-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;

    .field-label {
      display: flex;
      align-items: center;
      font-weight: 600;
      font-size: 0.875rem;
      color: var(--text-color);
      margin: 0;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  }

  .segments-summary {
    background: rgba(139, 92, 246, 0.1);
    border: 1px solid rgba(139, 92, 246, 0.5);
    border-radius: 6px;
    padding: 0.75rem 1rem;

    .segments-info {
      display: flex;
      align-items: center;
      font-size: 0.875rem;
      color: rgba(139, 92, 246, 1);
      font-weight: 500;
    }
  }

  .conditional-icon {
    margin-right: 0.5rem;
    font-size: 0.875rem;
    color: #8b5cf6;
  }

  .no-segments {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    background: var(--surface-0);
    border: 1px dashed var(--surface-300);
    border-radius: 6px;
    font-size: 0.875rem;
  }

  .btn-action-green {
    color: #94c840 !important;
  }
}
</style>
